<template>
  <div class="p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Estados de Recepción</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra los estados del flujo de recepción de equipos</p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Botón Exportar / Imprimir -->
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Icon icon="mdi:printer" class="mr-2" />
            Imprimir
            <Icon icon="mdi:chevron-down" class="ml-2" />
          </button>
          
          <!-- Menú Exportar / Imprimir -->
          <div v-if="showExportMenu" 
               class="absolute right-0 mt-2 w-56 bg-gray-900 rounded-xl shadow-lg border border-gray-700 z-50">
            <div class="py-2">
              <button
                @click="printFullReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-500" />
                <div>
                  <p class="font-medium">Reporte Completo</p>
                  <p class="text-xs text-gray-400">Con estadísticas y tabla</p>
                </div>
              </button>
              <button
                @click="printSimplifiedReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:table" class="mr-3 text-blue-500" />
                <div>
                  <p class="font-medium">Listado Simplificado</p>
                  <p class="text-xs text-gray-400">Solo tabla de estados</p>
                </div>
              </button>
              <div class="border-t border-gray-700 my-2"></div>
              <button
                @click="printCurrentView"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:eye" class="mr-3 text-green-500" />
                <div>
                  <p class="font-medium">Vista Actual</p>
                  <p class="text-xs text-gray-400">Con filtros aplicados</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <button
          @click="openCreateModal"
          class="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg"
        >
          <Icon icon="mdi:plus" class="mr-2" />
          Nuevo Estado
        </button>
      </div>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 mb-6 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div class="md:col-span-6 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Buscar estados
          </label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Nombre, descripción, color..."
              class="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              @input="handleSearch"
            />
          </div>
        </div>
        <div class="md:col-span-3 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Estado Activo
          </label>
          <div class="relative">
            <select
              v-model="filterActiveStatus"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
              @change="handleFilterChange"
            >
              <option value="all">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-3 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Color
          </label>
          <div class="relative">
            <select
              v-model="filterColor"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
              @change="handleFilterChange"
            >
              <option value="all">Todos los colores</option>
              <option value="success">Éxito (Verde)</option>
              <option value="warning">Advertencia (Amarillo)</option>
              <option value="danger">Peligro (Rojo)</option>
              <option value="info">Información (Celeste)</option>
              <option value="primary">Primario (Azul)</option>
              <option value="secondary">Secundario (Gris)</option>
              <option value="dark">Oscuro</option>
              <option value="light">Claro</option>
            </select>
            <Icon icon="mdi:palette" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-3 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ordenar por
          </label>
          <div class="relative">
            <select
              v-model="sortBy"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
            >
              <option value="orden">Orden (secuencia)</option>
              <option value="orden_desc">Orden (descendente)</option>
              <option value="nombre">Nombre (A-Z)</option>
              <option value="nombre_desc">Nombre (Z-A)</option>
              <option value="fechaCreacion_desc">Más recientes</option>
              <option value="fechaCreacion">Más antiguas</option>
              <option value="color">Color</option>
              <option value="estado">Estado Activo</option>
            </select>
            <Icon icon="mdi:sort" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-6 lg:col-span-3 flex items-end gap-2">
          <button
            @click="loadEstados"
            class="flex-1 flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-700 transition-all duration-300 shadow hover:shadow-md"
          >
            <Icon icon="mdi:refresh" class="mr-2" :class="{ 'animate-spin': loading }" />
            {{ loading ? 'Cargando...' : 'Actualizar' }}
          </button>
          <button
            v-if="sortBy === 'orden'"
            @click="openReorderModal"
            class="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow hover:shadow-md"
            title="Reordenar estados"
          >
            <Icon icon="mdi:sort-variant" class="text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-5 rounded-xl border border-blue-200 dark:border-blue-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
            <Icon icon="mdi:state-machine" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Estados</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estados.length }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                {{ filteredEstados.length }} filtrados
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-5 rounded-xl border border-green-200 dark:border-green-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-md">
            <Icon icon="mdi:check-circle" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Estados Activos</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.activos }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
                {{ estadisticas.porcentajeActivos }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-5 rounded-xl border border-red-200 dark:border-red-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md">
            <Icon icon="mdi:close-circle" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Estados Inactivos</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.inactivos }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full">
                {{ estadisticas.porcentajeInactivos }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-5 rounded-xl border border-purple-200 dark:border-purple-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md">
            <Icon icon="mdi:format-list-numbered" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Rango de Órdenes</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticas.minOrden }} - {{ estadisticas.maxOrden }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ estadisticas.conDescripcion }} con descripción
            </p>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-5 rounded-xl border border-amber-200 dark:border-amber-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow-md">
            <Icon icon="mdi:palette" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Colores</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ Object.keys(estadisticas.colores).length }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ estadisticas.recientes }} recientes
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterActiveStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterActiveStatus === 'activos' ? 'Activos' : 'Inactivos' }}
          <button @click="filterActiveStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterColor !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
          Color: {{ getColorName(filterColor) }}
          <button @click="filterColor = 'all'; handleFilterChange()" class="ml-1.5 hover:text-purple-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de estados -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Estados de Recepción</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredEstados.length }} de {{ estados.length }} estados
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Orden</span>
                  <button @click="sortBy = sortBy === 'orden' ? 'orden_desc' : 'orden'">
                    <Icon icon="mdi:sort-numeric" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Estado</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Descripción
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Color
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Estado Activo
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Fecha Registro
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="7" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando estados...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredEstados.length === 0">
              <td colspan="7" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:state-machine-off" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron estados de recepción
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterActiveStatus !== 'all' || filterColor !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay estados registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterActiveStatus === 'all' && filterColor === 'all'"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer estado
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <!-- Lista de estados -->
            <tr 
              v-else 
              v-for="estado in paginatedEstados" 
              :key="estado.idEstadoRecepcion"
              class="transition-all duration-200 cursor-pointer border-l-4"
              :class="[
                `border-${getColorClass(estado.color)}-500 hover:bg-${getColorClass(estado.color)}-50/50 dark:hover:bg-${getColorClass(estado.color)}-900/20`,
                estado.estado !== 'activo' ? 'opacity-75 grayscale-[0.3]' : ''
              ]"
              @click="openDetailModal(estado)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center justify-center">
                  <div :class="[
                    'w-10 h-10 rounded-full flex items-center justify-center shadow-sm border-2 font-black text-lg',
                    `bg-${getColorClass(estado.color)}-50 border-${getColorClass(estado.color)}-200 text-${getColorClass(estado.color)}-600 dark:bg-${getColorClass(estado.color)}-900/30 dark:border-${getColorClass(estado.color)}-700 dark:text-${getColorClass(estado.color)}-400`
                  ]">
                    <span>{{ estado.orden }}</span>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-12 w-12">
                    <div :class="[
                      'h-12 w-12 rounded-xl flex items-center justify-center shadow-sm',
                      `bg-${getColorClass(estado.color)}-100 dark:bg-${getColorClass(estado.color)}-900/50`
                    ]">
                      <Icon 
                        icon="mdi:progress-clock" 
                        :class="[
                          'text-xl',
                          `text-${getColorClass(estado.color)}-600 dark:text-${getColorClass(estado.color)}-400`
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div :class="[
                      'text-sm font-black tracking-tight',
                      `text-${getColorClass(estado.color)}-700 dark:text-${getColorClass(estado.color)}-400`
                    ]">
                      {{ formatearNombre(estado.nombre).toUpperCase() }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                      <Icon icon="mdi:identifier" class="mr-1" />
                      ID: {{ estado.idEstadoRecepcion }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <p class="text-sm text-gray-900 dark:text-white max-w-md">
                  {{ estado.descripcion || 'Sin descripción' }}
                </p>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="[
                  'px-3 py-1.5 inline-flex text-[10px] uppercase tracking-wider font-black rounded-lg shadow-sm border-2',
                  `bg-${getColorClass(estado.color)}-500 text-white border-${getColorClass(estado.color)}-600 dark:border-${getColorClass(estado.color)}-400`
                ]">
                  <Icon :icon="getColorIcon(estado.color)" class="mr-1.5 text-sm" />
                  {{ getColorName(estado.color).split(' ')[0] }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="toggleActiveStatus(estado)"
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    estado.estado?.toLowerCase() === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200'
                  ]"
                >
                  <Icon :icon="estado.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5" />
                  {{ estado.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">
                  {{ formatDate(estado.fecha_creacion || estado.fechaCreacion) }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatTime(estado.fecha_creacion || estado.fechaCreacion) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="openEditModal(estado)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    @click.stop="confirmDelete(estado)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredEstados.length > 0 && !loading" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-sm text-gray-700 dark:text-gray-300">
          Mostrando <span class="font-semibold">{{ startItem }}-{{ endItem }}</span> de <span class="font-semibold">{{ filteredEstados.length }}</span> resultados
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="currentPage = Math.max(currentPage - 1, 1)"
            :disabled="currentPage === 1"
            :class="[
              'px-3 py-1 rounded-lg border transition-colors',
              currentPage === 1
                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          
          <div class="flex space-x-1">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="currentPage = page"
              :class="[
                'w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-colors',
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              ]"
            >
              {{ page }}
            </button>
          </div>
          
          <button
            @click="currentPage = Math.min(currentPage + 1, totalPages)"
            :disabled="currentPage === totalPages"
            :class="[
              'px-3 py-1 rounded-lg border transition-colors',
              currentPage === totalPages
                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar estado -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
        <!-- Encabezado del modal -->
        <div class="px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md mr-4">
                <Icon :icon="editingEstado ? 'mdi:pencil-box' : 'mdi:progress-clock'" class="text-white text-2xl" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                  {{ editingEstado ? 'Editar Estado de Recepción' : 'Nuevo Estado de Recepción' }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {{ editingEstado ? 'Modifica los datos del estado' : 'Completa los datos para crear un nuevo estado' }}
                </p>
              </div>
            </div>
            <button 
              @click="closeModal"
              class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
        </div>
        
        <!-- Formulario -->
        <form @submit.prevent="saveEstado" class="px-8 py-6 max-h-[80vh] overflow-y-auto">
          <div class="space-y-6">
            <!-- Nombre -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del estado *
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:tag" class="text-gray-400" />
                </div>
                <input
                  v-model="form.nombre"
                  type="text"
                  required
                  maxlength="50"
                  class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                  placeholder="Ej: recibido, diagnostico, reparando..."
                  :class="{ 'border-red-500': errors.nombre }"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <span class="text-xs text-gray-500">{{ form.nombre.length }}/50</span>
                </div>
              </div>
              <p v-if="errors.nombre" class="mt-2 text-sm text-red-600 dark:text-red-400">
                {{ errors.nombre }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Usa letras minúsculas y guiones bajos para nombres compuestos (ej: esperando_repuesto)
              </p>
            </div>
            
            <!-- Descripción -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <div class="relative">
                <div class="absolute top-3 left-3 pointer-events-none">
                  <Icon icon="mdi:text-box" class="text-gray-400" />
                </div>
                <textarea
                  v-model="form.descripcion"
                  rows="3"
                  maxlength="200"
                  class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 resize-none"
                  placeholder="Describe brevemente el estado..."
                ></textarea>
                <div class="absolute bottom-3 right-3">
                  <span class="text-xs text-gray-500">{{ form.descripcion.length }}/200</span>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Esta descripción será visible en el flujo de recepción.
              </p>
            </div>

            <!-- Orden -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Orden en el flujo *
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:format-list-numbered" class="text-gray-400" />
                </div>
                <input
                  v-model.number="form.orden"
                  type="number"
                  min="1"
                  step="1"
                  required
                  class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                  :class="{ 'border-red-500': errors.orden }"
                />
              </div>
              <p v-if="errors.orden" class="mt-2 text-sm text-red-600 dark:text-red-400">
                {{ errors.orden }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Define la secuencia del estado en el flujo de trabajo (1 = primero, 2 = segundo, etc.)
              </p>
            </div>
            
            <!-- Color -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Color *
              </label>
              <div class="grid grid-cols-4 gap-3">
                <label v-for="colorOption in colorOptions" :key="colorOption.value"
                  class="flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.color === colorOption.value 
                    ? `border-${colorOption.colorClass}-500 bg-${colorOption.colorClass}-50 dark:bg-${colorOption.colorClass}-900/30` 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.color" :value="colorOption.value" class="sr-only">
                  <div class="flex flex-col items-center">
                    <div :class="[
                      'w-8 h-8 rounded-full mb-1',
                      `bg-${colorOption.colorClass}-500`
                    ]"></div>
                    <span class="text-xs text-gray-700 dark:text-gray-300">{{ colorOption.label }}</span>
                  </div>
                </label>
              </div>
              <p v-if="errors.color" class="mt-2 text-sm text-red-600 dark:text-red-400">
                {{ errors.color }}
              </p>
            </div>
            
            <!-- Estado Activo -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado del registro
              </label>
              <div class="grid grid-cols-2 gap-3">
                <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.estado === 'activo' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.estado" value="activo" class="sr-only">
                  <div class="flex items-center w-full">
                    <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                      :class="form.estado === 'activo' 
                        ? 'border-green-500 bg-green-500' 
                        : 'border-gray-400'">
                      <Icon v-if="form.estado === 'activo'" icon="mdi:check" class="text-white text-xs" />
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">Activo</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">Disponible en el sistema</p>
                    </div>
                    <Icon icon="mdi:check-circle" class="ml-auto text-green-500" v-if="form.estado === 'activo'" />
                  </div>
                </label>
                
                <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.estado === 'inactivo' 
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/30' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.estado" value="inactivo" class="sr-only">
                  <div class="flex items-center w-full">
                    <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                      :class="form.estado === 'inactivo' 
                        ? 'border-red-500 bg-red-500' 
                        : 'border-gray-400'">
                      <Icon v-if="form.estado === 'inactivo'" icon="mdi:check" class="text-white text-xs" />
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">Inactivo</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">No disponible</p>
                    </div>
                    <Icon icon="mdi:close-circle" class="ml-auto text-red-500" v-if="form.estado === 'inactivo'" />
                  </div>
                </label>
              </div>
            </div>
            
            <!-- Previsualización -->
            <div v-if="form.nombre" class="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Vista previa:</p>
              <div class="flex items-start space-x-4">
                <div :class="[
                  'p-3 rounded-lg',
                  `bg-${getColorClass(form.color)}-100 dark:bg-${getColorClass(form.color)}-900/50`
                ]">
                  <Icon icon="mdi:progress-clock" :class="[
                    'text-xl',
                    `text-${getColorClass(form.color)}-600 dark:text-${getColorClass(form.color)}-400`
                  ]" />
                </div>
                <div class="flex-1">
                  <div class="flex items-start justify-between">
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">{{ formatearNombre(form.nombre) }}</p>
                      <p class="text-sm text-gray-500 dark:text-gray-400">
                        Orden: {{ form.orden }}
                      </p>
                      <p v-if="form.descripcion" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {{ form.descripcion }}
                      </p>
                      <p v-else class="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">
                        Sin descripción
                      </p>
                    </div>
                    <span :class="[
                      'px-3 py-1 text-xs font-semibold rounded-full',
                      form.estado === 'activo'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    ]">
                      {{ form.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Botones -->
          <div class="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              @click="closeModal"
              class="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 hover:shadow-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving || !isFormValid"
              :class="[
                'px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center',
                isFormValid && !saving
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              ]"
            >
              <span v-if="saving" class="flex items-center">
                <Icon icon="mdi:loading" class="animate-spin mr-2" />
                Guardando...
              </span>
              <span v-else class="flex items-center">
                <Icon :icon="editingEstado ? 'mdi:content-save' : 'mdi:plus-circle'" class="mr-2" />
                {{ editingEstado ? 'Actualizar' : 'Crear' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de detalles -->
    <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div class="p-8">
          <div class="flex justify-between items-start mb-6">
            <div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Detalles del Estado</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">Información completa del estado de recepción</p>
            </div>
            <button @click="closeDetailModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Icon icon="mdi:close" class="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div v-if="selectedEstado" class="space-y-6">
            <div class="flex items-center space-x-4">
              <div :class="[
                'p-4 rounded-xl',
                `bg-${getColorClass(selectedEstado.color)}-100 dark:bg-${getColorClass(selectedEstado.color)}-900/50`
              ]">
                <Icon icon="mdi:progress-clock" :class="[
                  'text-2xl',
                  `text-${getColorClass(selectedEstado.color)}-600 dark:text-${getColorClass(selectedEstado.color)}-400`
                ]" />
              </div>
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white">{{ formatearNombre(selectedEstado.nombre) }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">ID: {{ selectedEstado.idEstadoRecepcion }}</p>
              </div>
            </div>
            
            <div class="space-y-4">
              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descripción</label>
                <p class="mt-1 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {{ selectedEstado.descripcion || 'Sin descripción' }}
                </p>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</label>
                  <p class="mt-1 text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {{ selectedEstado.orden }}
                  </p>
                </div>
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Color</label>
                  <div class="mt-1">
                    <span :class="[
                      'px-4 py-2 inline-flex text-sm font-semibold rounded-full',
                      `bg-${getColorClass(selectedEstado.color)}-100 dark:bg-${getColorClass(selectedEstado.color)}-900 text-${getColorClass(selectedEstado.color)}-800 dark:text-${getColorClass(selectedEstado.color)}-200`
                    ]">
                      <Icon :icon="getColorIcon(selectedEstado.color)" class="mr-2" />
                      {{ getColorName(selectedEstado.color) }}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado Activo</label>
                <div class="mt-1">
                  <span :class="[
                    'px-4 py-2 inline-flex text-sm font-semibold rounded-full',
                    selectedEstado.estado === 'activo'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  ]">
                    <Icon :icon="selectedEstado.estado === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-2" />
                    {{ selectedEstado.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                  </span>
                </div>
              </div>
              
              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de Creación</label>
                <div class="mt-1 flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Icon icon="mdi:calendar" class="text-gray-500" />
                  <div>
                    <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(selectedEstado.fecha_creacion || selectedEstado.fechaCreacion) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(selectedEstado.fecha_creacion || selectedEstado.fechaCreacion) }}</p>
                  </div>
                </div>
              </div>
              
              <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div class="flex justify-end space-x-3">
                  <button
                    @click="openEditModal(selectedEstado); closeDetailModal()"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar
                  </button>
                  <button
                    @click="closeDetailModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de reordenamiento -->
    <div v-if="showReorderModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl">
        <div class="p-6">
          <div class="flex justify-between items-start mb-6">
            <div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Reordenar Estados</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">Arrastra los estados para cambiar su orden en el flujo de trabajo</p>
            </div>
            <button @click="closeReorderModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Icon icon="mdi:close" class="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div class="space-y-3 max-h-[60vh] overflow-y-auto">
            <div
              v-for="(estado, index) in reorderList"
              :key="estado.idEstadoRecepcion"
              draggable="true"
              @dragstart="dragStart(index)"
              @dragover.prevent
              @drop="drop(index)"
              class="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-move hover:shadow-md transition-shadow"
            >
              <div class="flex-shrink-0 mr-4">
                <Icon icon="mdi:drag-vertical" class="text-2xl text-gray-400" />
              </div>
              <div class="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                {{ index + 1 }}
              </div>
              <div class="ml-4 flex-1">
                <div class="flex items-center">
                  <div :class="[
                    'w-8 h-8 rounded-lg flex items-center justify-center mr-3',
                    `bg-${getColorClass(estado.color)}-100 dark:bg-${getColorClass(estado.color)}-900/50`
                  ]">
                    <Icon icon="mdi:progress-clock" :class="[
                      'text-sm',
                      `text-${getColorClass(estado.color)}-600 dark:text-${getColorClass(estado.color)}-400`
                    ]" />
                  </div>
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">{{ formatearNombre(estado.nombre) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ estado.descripcion || 'Sin descripción' }}</p>
                  </div>
                </div>
              </div>
              <div class="flex-shrink-0">
                <span :class="[
                  'px-2 py-1 text-xs rounded-full',
                  estado.estado === 'activo'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                ]">
                  {{ estado.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
            </div>
          </div>
          
          <div class="mt-6 flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              @click="closeReorderModal"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              @click="saveReorder"
              :disabled="savingReorder"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Icon v-if="savingReorder" icon="mdi:loading" class="animate-spin mr-2" />
              Guardar orden
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmación Personalizado -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60] transition-opacity duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
        <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div :class="[
              'p-2 rounded-full',
              confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 
              confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' : 
              'bg-blue-100 dark:bg-blue-900/30'
            ]">
              <Icon 
                :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' : 
                       confirmConfig.type === 'warning' ? 'mdi:alert' : 
                       'mdi:information'" 
                :class="[
                  'text-2xl',
                  confirmConfig.type === 'danger' ? 'text-red-600 dark:text-red-400' : 
                  confirmConfig.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 
                  'text-blue-600 dark:text-blue-400'
                ]"
              />
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ confirmConfig.title }}</h3>
          </div>
        </div>

        <div class="px-6 py-4">
          <p class="text-gray-700 dark:text-gray-300" v-html="confirmConfig.message"></p>
          <p v-if="confirmConfig.detail" class="text-sm text-gray-500 dark:text-gray-400 mt-2">{{ confirmConfig.detail }}</p>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            @click="cancelConfirm"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {{ confirmConfig.cancelText }}
          </button>
          <button
            @click="executeConfirm"
            :class="[
              'px-4 py-2 rounded-lg text-white font-medium transition-colors',
              confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 
              confirmConfig.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700' : 
              'bg-blue-600 hover:bg-blue-700'
            ]"
          >
            {{ confirmConfig.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

// Importar funciones de la API de estados de recepción
import {
  obtenerEstadosRecepcion,
  obtenerEstadosRecepcionOrdenados,
  crearEstadoRecepcion,
  actualizarEstadoRecepcion,
  eliminarEstadoRecepcion,
  activarEstadoRecepcion,
  desactivarEstadoRecepcion,
  ordenarEstadosRecepcion,
  verificarEstadoRecepcionPorNombre,
  buscarEstadosLocal,
  filtrarEstadosPorEstadoLocal,
  obtenerEstadisticasEstadosRecepcion,
  ordenarEstados,
  formatearNombre,
  formatearFecha,
  formatearDescripcion,
  obtenerClaseColor,
  getColorName as getColorNameUtil,
  getColorIcon as getColorIconUtil
} from '@/api/estadoRecepcion.js';

// Importar generador de reportes
import {
  imprimirReporteEstadosRecepcion,
  imprimirReporteSimplificadoEstadosRecepcion
} from '@/utils/reporteEstadosRecepcionGenerator.js';

export default {
  name: 'EstadosRecepcionList',
  components: {
    Icon,
  },
  data() {
    return {
      estados: [],
      loading: false,
      saving: false,
      savingReorder: false,
      showModal: false,
      showDetailModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      showReorderModal: false,
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterActiveStatus: 'all',
      filterColor: 'all',
      sortBy: 'orden',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idEstadoRecepcion: null,
        nombre: '',
        descripcion: '',
        orden: 1,
        color: 'secondary',
        estado: 'activo',
      },
      errors: {},
      editingEstado: null,
      selectedEstado: null,
      reorderList: [],
      dragIndex: null,
      lastUpdate: new Date(),
      colorOptions: [
        { label: 'Éxito (Verde)', value: 'success', colorClass: 'green' },
        { label: 'Advertencia', value: 'warning', colorClass: 'amber' },
        { label: 'Peligro (Rojo)', value: 'danger', colorClass: 'red' },
        { label: 'Información', value: 'info', colorClass: 'cyan' },
        { label: 'Primario (Azul)', value: 'primary', colorClass: 'blue' },
        { label: 'Morado', value: 'purple', colorClass: 'purple' },
        { label: 'Índigo', value: 'indigo', colorClass: 'indigo' },
        { label: 'Rosado', value: 'pink', colorClass: 'pink' },
        { label: 'Naranja', value: 'orange', colorClass: 'orange' },
        { label: 'Teal', value: 'teal', colorClass: 'teal' },
        { label: 'Fucsia', value: 'fuchsia', colorClass: 'fuchsia' },
        { label: 'Lima', value: 'lime', colorClass: 'lime' },
        { label: 'Esmeralda', value: 'emerald', colorClass: 'emerald' },
        { label: 'Rosa', value: 'rose', colorClass: 'rose' },
        { label: 'Cian', value: 'cyan', colorClass: 'cyan' },
        { label: 'Sky', value: 'sky', colorClass: 'sky' },
        { label: 'Violeta', value: 'violet', colorClass: 'violet' },
        { label: 'Secundario', value: 'secondary', colorClass: 'gray' },
        { label: 'Oscuro', value: 'dark', colorClass: 'gray' },
        { label: 'Claro', value: 'light', colorClass: 'gray' }
      ]
    };
  },
  computed: {
    filteredEstados() {
      let filtered = [...this.estados];
      
      if (this.searchQuery.trim()) {
        filtered = buscarEstadosLocal(filtered, this.searchQuery);
      }
      
      if (this.filterActiveStatus !== 'all') {
        filtered = filtrarEstadosPorEstadoLocal(filtered, this.filterActiveStatus);
      }
      
      if (this.filterColor !== 'all') {
        filtered = filtered.filter(e => e.color === this.filterColor);
      }
      
      filtered = ordenarEstados(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticasEstadosRecepcion(this.estados);
    },
    
    paginatedEstados() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredEstados.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredEstados.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredEstados.length);
    },
    
    isFormValid() {
      return this.form.nombre.trim().length > 0 && this.form.orden > 0 && this.form.color;
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterActiveStatus !== 'all' || this.filterColor !== 'all';
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.loadEstados();
  },
  watch: {
    filterActiveStatus() {
      this.currentPage = 1;
    },
    filterColor() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatearNombre,
    formatearDescripcion,
    
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },
    
    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) {
        this.confirmConfig.onCancel();
      }
    },
    
    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) {
        this.confirmConfig.onConfirm();
      }
    },
    
    async loadEstados() {
      this.loading = true;
      try {
        const response = await obtenerEstadosRecepcion();
        
        if (response.success) {
          this.estados = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Estados de recepción cargados:', this.estados.length);
        } else {
          throw new Error(response.error || 'Error al cargar estados');
        }
      } catch (error) {
        console.error('❌ Error al cargar estados:', error);
        this.toast.error('Error al cargar los estados: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openCreateModal() {
      this.editingEstado = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },
    
    openEditModal(estado) {
      this.editingEstado = estado;
      this.form = {
        idEstadoRecepcion: estado.idEstadoRecepcion,
        nombre: estado.nombre,
        descripcion: estado.descripcion || '',
        orden: estado.orden || 1,
        color: estado.color || 'secondary',
        estado: estado.estado || 'activo',
      };
      this.errors = {};
      this.showModal = true;
    },
    
    openDetailModal(estado) {
      this.selectedEstado = estado;
      this.showDetailModal = true;
    },
    
    openReorderModal() {
      this.reorderList = [...this.estados].sort((a, b) => (a.orden || 0) - (b.orden || 0));
      this.showReorderModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedEstado = null;
    },
    
    closeReorderModal() {
      this.showReorderModal = false;
      this.reorderList = [];
      this.dragIndex = null;
    },
    
    resetForm() {
      const maxOrden = this.estados.length > 0 ? Math.max(...this.estados.map(e => e.orden || 0)) : 0;
      this.form = {
        idEstadoRecepcion: null,
        nombre: '',
        descripcion: '',
        orden: maxOrden + 1,
        color: 'secondary',
        estado: 'activo',
      };
      this.editingEstado = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 50) {
        this.errors.nombre = 'El nombre no puede exceder 50 caracteres';
      } else if (!/^[a-záéíóúñ_]+$/i.test(this.form.nombre)) {
        this.errors.nombre = 'El nombre solo puede contener letras y guiones bajos';
      }
      
      if (this.form.descripcion && this.form.descripcion.length > 200) {
        this.errors.descripcion = 'La descripción no puede exceder 200 caracteres';
      }
      
      if (!this.form.orden || this.form.orden < 1) {
        this.errors.orden = 'El orden debe ser mayor a 0';
      }
      
      if (!this.form.color) {
        this.errors.color = 'Debes seleccionar un color';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async saveEstado() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        if (!this.editingEstado) {
          const verifyResult = await verificarEstadoRecepcionPorNombre(this.form.nombre);
          
          if (verifyResult.success && verifyResult.existe) {
            this.errors.nombre = 'Este estado ya está registrado';
            this.saving = false;
            return;
          }
        }

        const estadoData = {
          nombre: this.form.nombre.trim().toLowerCase(),
          descripcion: this.form.descripcion.trim() || null,
          orden: parseInt(this.form.orden),
          color: this.form.color,
          estado: this.form.estado
        };

        let result;
        if (this.editingEstado) {
          result = await actualizarEstadoRecepcion(this.editingEstado.idEstadoRecepcion, estadoData);
        } else {
          result = await crearEstadoRecepcion(estadoData);
        }

        if (result.success) {
          await this.loadEstados();
          this.closeModal();
          this.toast.success(
            this.editingEstado ? 'Estado actualizado correctamente' : 'Estado creado correctamente'
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar estado:', error);
        this.toast.error(error.message || 'Error al guardar el estado');
      } finally {
        this.saving = false;
      }
    },
    
    async confirmDelete(estado) {
      const confirmed = await this.showConfirm({
        title: '¿Eliminar estado?',
        message: `¿Estás seguro de eliminar <strong>"${this.formatearNombre(estado.nombre)}"</strong>?`,
        detail: 'Esta acción no se puede deshacer. Si hay recepciones asociadas a este estado, no podrás eliminarlo.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });
      
      if (confirmed) {
        await this.deleteEstado(estado.idEstadoRecepcion, estado.nombre);
      }
    },
    
    async deleteEstado(id, nombre) {
      try {
        const result = await eliminarEstadoRecepcion(id);
        if (result.success) {
          await this.loadEstados();
          this.toast.success(`El estado "${this.formatearNombre(nombre)}" ha sido eliminado`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar estado:', error);
        this.toast.error(error.message || 'Error al eliminar el estado');
      }
    },
    
    async toggleActiveStatus(estado) {
      const esActivo = estado.estado === 'activo';
      
      const confirmed = await this.showConfirm({
        title: `${!esActivo ? 'Activar' : 'Desactivar'} estado`,
        message: `¿Deseas ${!esActivo ? 'activar' : 'desactivar'} el estado <strong>"${this.formatearNombre(estado.nombre)}"</strong>?`,
        confirmText: `Sí, ${!esActivo ? 'activar' : 'desactivar'}`,
        cancelText: 'Cancelar',
        type: 'warning'
      });
      
      if (confirmed) {
        try {
          let result;
          if (!esActivo) {
            result = await activarEstadoRecepcion(estado.idEstadoRecepcion);
          } else {
            result = await desactivarEstadoRecepcion(estado.idEstadoRecepcion);
          }
          
          if (result.success) {
            await this.loadEstados();
            this.toast.success(`Estado ${!esActivo ? 'activado' : 'desactivado'} correctamente`);
          } else {
            throw new Error(result.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error('Error al cambiar el estado');
        }
      }
    },
    
    dragStart(index) {
      this.dragIndex = index;
    },
    
    drop(index) {
      if (this.dragIndex === null || this.dragIndex === index) {
        return;
      }
      
      const item = this.reorderList[this.dragIndex];
      this.reorderList.splice(this.dragIndex, 1);
      this.reorderList.splice(index, 0, item);
      this.dragIndex = null;
    },
    
    async saveReorder() {
      this.savingReorder = true;
      try {
        const ordenes = this.reorderList.map((estado, idx) => ({
          id: estado.idEstadoRecepcion,
          orden: idx + 1
        }));
        
        const result = await ordenarEstadosRecepcion(ordenes);
        
        if (result.success) {
          await this.loadEstados();
          this.closeReorderModal();
          this.toast.success('Orden de estados actualizado correctamente');
        } else {
          throw new Error(result.error || 'Error al ordenar');
        }
      } catch (error) {
        console.error('❌ Error al reordenar:', error);
        this.toast.error(error.message || 'Error al guardar el orden');
      } finally {
        this.savingReorder = false;
      }
    },
    
    printFullReport() {
      if (this.filteredEstados.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        const filtros = {
          estadoActivo: this.filterActiveStatus,
          color: this.filterColor,
          busqueda: this.searchQuery,
          orden: this.sortBy
        };
        
        imprimirReporteEstadosRecepcion(
          this.filteredEstados,
          'REPORTE DE ESTADOS DE RECEPCIÓN - JEAN INFORMATICO',
          filtros
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printSimplifiedReport() {
      if (this.filteredEstados.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        imprimirReporteSimplificadoEstadosRecepcion(
          this.filteredEstados,
          'LISTADO DE ESTADOS DE RECEPCIÓN - JEAN INFORMATICO'
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printCurrentView() {
      this.printFullReport();
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterActiveStatus = 'all';
      this.filterColor = 'all';
      this.sortBy = 'orden';
      this.currentPage = 1;
    },
    
    getColorName(color) {
      const colors = {
        success: 'Éxito (Verde)',
        warning: 'Advertencia',
        danger: 'Peligro (Rojo)',
        info: 'Información',
        primary: 'Primario (Azul)',
        secondary: 'Secundario',
        dark: 'Oscuro',
        light: 'Claro',
        purple: 'Morado',
        indigo: 'Índigo',
        pink: 'Rosado',
        orange: 'Naranja',
        teal: 'Teal',
        fuchsia: 'Fucsia',
        lime: 'Lima',
        emerald: 'Esmeralda',
        rose: 'Rosa',
        cyan: 'Cian',
        sky: 'Sky',
        violet: 'Violeta'
      };
      return colors[color] || color || 'Secundario';
    },
    
    getColorClass(color) {
      const classes = {
        success: 'green',
        warning: 'amber',
        danger: 'red',
        info: 'cyan',
        primary: 'blue',
        secondary: 'gray',
        dark: 'gray',
        light: 'gray',
        purple: 'purple',
        indigo: 'indigo',
        pink: 'pink',
        orange: 'orange',
        teal: 'teal',
        fuchsia: 'fuchsia',
        lime: 'lime',
        emerald: 'emerald',
        rose: 'rose',
        cyan: 'cyan',
        sky: 'sky',
        violet: 'violet'
      };
      return classes[color] || 'gray';
    },
    
    getColorIcon(color) {
      const icons = {
        success: 'mdi:check-circle',
        warning: 'mdi:alert',
        danger: 'mdi:close-circle',
        info: 'mdi:information',
        primary: 'mdi:star',
        secondary: 'mdi:circle',
        dark: 'mdi:circle',
        light: 'mdi:circle',
        purple: 'mdi:crown',
        indigo: 'mdi:shield',
        pink: 'mdi:heart',
        orange: 'mdi:fire',
        teal: 'mdi:water',
        fuchsia: 'mdi:auto-fix',
        lime: 'mdi:leaf',
        emerald: 'mdi:diamond',
        rose: 'mdi:flower',
        cyan: 'mdi:cloud',
        sky: 'mdi:weather-sunny',
        violet: 'mdi:magic-staff'
      };
      return icons[color] || 'mdi:circle';
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

[draggable="true"] {
  user-select: none;
  cursor: move;
}
</style>